import type * as v from "valibot";
import {
  type CollectionDto,
  getArticlesReadApi,
  getBookmarkedArticlesApi,
  getFollowingApi,
  getUserProfileApi,
  type getUserProfileResponse,
  listCollectionsApi,
} from "../api";
import { DEFAULT_SETTINGS, type UserSettings, type UserTips } from "../settings";
import { CACHE_KEYS, CACHE_TTL_DAY, CACHE_TTL_HOUR } from "./cache";
import { type CacheItem, cacheItem } from "./cacheItem";

export type UserProfile = v.InferOutput<v.ObjectSchema<typeof getUserProfileResponse, undefined>>;

/** "authorAlias:articleId" to the ids of the collections that hold it. */
export type BookmarkedArticles = Record<string, string[]>;

const defaultSettings = (): UserSettings => ({ ...DEFAULT_SETTINGS }) as UserSettings;

const fetchProfile = async (): Promise<UserProfile | null> => {
  const profile = await getUserProfileApi.invoke({});
  // Settings and tips live beside the profile and outlive it, so a fresh profile is their source of truth.
  items.settings.set({ ...defaultSettings(), ...profile.settings });
  items.tips.set(profile.tips);
  return profile;
};

type Items = {
  profile: CacheItem<UserProfile | null>;
  settings: CacheItem<UserSettings>;
  tips: CacheItem<UserTips>;
  following: CacheItem<string[]>;
  articlesRead: CacheItem<string[]>;
  bookmarked: CacheItem<BookmarkedArticles>;
  collections: CacheItem<CollectionDto[]>;
};

/** The cache items the client keeps. Prefer the functions of this package over using them directly. */
export const items: Items = {
  profile: cacheItem<UserProfile | null>({ key: CACHE_KEYS.profile, ttl: CACHE_TTL_HOUR, supplier: fetchProfile, fallback: () => null }),
  settings: cacheItem<UserSettings>({
    key: CACHE_KEYS.settings,
    supplier: async () => ({ ...defaultSettings(), ...((await items.profile.get())?.settings ?? {}) }),
    fallback: defaultSettings,
    requiresSignIn: false,
  }),
  tips: cacheItem<UserTips>({
    key: CACHE_KEYS.tips,
    supplier: async () => (await items.profile.get())?.tips ?? {},
    fallback: () => ({}),
    requiresSignIn: false,
  }),
  following: cacheItem<string[]>({
    key: CACHE_KEYS.following,
    ttl: CACHE_TTL_DAY,
    supplier: async () => (await getFollowingApi.invoke({})).authors,
    fallback: () => [],
  }),
  articlesRead: cacheItem<string[]>({
    key: CACHE_KEYS.articlesRead,
    ttl: CACHE_TTL_DAY,
    supplier: async () => (await getArticlesReadApi.invoke({})).articleIds,
    fallback: () => [],
  }),
  bookmarked: cacheItem<BookmarkedArticles>({
    key: CACHE_KEYS.bookmarked,
    ttl: CACHE_TTL_HOUR,
    supplier: async () => (await getBookmarkedArticlesApi.invoke({})).articleCollections,
    fallback: () => ({}),
  }),
  collections: cacheItem<CollectionDto[]>({
    key: CACHE_KEYS.collections,
    ttl: CACHE_TTL_HOUR,
    supplier: async () => (await listCollectionsApi.invoke({})).collections,
    fallback: () => [],
  }),
};
